import type { FptEntitySyncMetadata } from "../models/production";
import type { Asset, Shot } from "../types";
import { getBidStatusValue } from "../lib/bidStatus";
import type { PulledFptTaskState } from "../functions/fpt/pullProductionState";

type SyncableEntity = Shot | Asset;

export interface InvoiceReadinessResult {
  invoiceReady: boolean;
  invoiceReadyReason?: string;
  invoiceBlockedReason?: string;
  completedInFpt: boolean;
  alreadyInvoiced: boolean;
}

const COMPLETE_FPT_STATUSES = new Set(["fin", "final", "dlvr", "delivered", "complete", "completed"]);

function normalizeStatus(value?: string | null) {
  return String(value || "").trim().toLowerCase();
}

function isCompleteStatus(value?: string | null) {
  return COMPLETE_FPT_STATUSES.has(normalizeStatus(value));
}

function isCommerciallyEligible(item: SyncableEntity) {
  const bidStatus = getBidStatusValue({ active: item.active, status: item.status });
  const normalized = normalizeStatus(bidStatus);
  return normalized === "awarded" || normalized === "overage";
}

export function computeInvoiceReadiness(
  item: SyncableEntity,
  metadata?: FptEntitySyncMetadata,
  taskStates?: PulledFptTaskState[]
): InvoiceReadinessResult {
  const bidStatus = getBidStatusValue({ active: item.active, status: item.status });
  const normalizedBidStatus = normalizeStatus(bidStatus);
  const alreadyInvoiced = normalizedBidStatus === "invoiced";

  if (alreadyInvoiced) {
    return {
      invoiceReady: false,
      completedInFpt: true,
      alreadyInvoiced: true,
      invoiceBlockedReason: "Already invoiced in the app.",
    };
  }

  if (!isCommerciallyEligible(item)) {
    return {
      invoiceReady: false,
      completedInFpt: false,
      alreadyInvoiced: false,
      invoiceBlockedReason: `Blocked: commercial status is ${bidStatus}.`,
    };
  }

  if (metadata?.pipelineStepStatus === "missing" || Number(metadata?.tasksMissingPipelineStep || 0) > 0) {
    return {
      invoiceReady: false,
      completedInFpt: false,
      alreadyInvoiced: false,
      invoiceBlockedReason: "Blocked: missing pipeline step.",
    };
  }

  if (metadata?.syncStatus === "error") {
    return {
      invoiceReady: false,
      completedInFpt: false,
      alreadyInvoiced: false,
      invoiceBlockedReason: "Blocked: sync error.",
    };
  }

  if (metadata?.conflictFlag) {
    return {
      invoiceReady: false,
      completedInFpt: false,
      alreadyInvoiced: false,
      invoiceBlockedReason: "Blocked: unresolved sync conflict.",
    };
  }

  const completeByEntityStatus = isCompleteStatus(metadata?.lastKnownFptStatus || item.progressStatus);
  const completeByTasks =
    Array.isArray(taskStates) &&
    taskStates.length > 0 &&
    taskStates.every((task) => isCompleteStatus(task.fptStatus));
  const completedInFpt = completeByEntityStatus || completeByTasks;

  if (!metadata?.lastPulledAt && !metadata?.lastKnownFptStatus && !(Array.isArray(taskStates) && taskStates.length > 0)) {
    return {
      invoiceReady: false,
      completedInFpt: false,
      alreadyInvoiced: false,
      invoiceBlockedReason: "Blocked: no FPT completion data pulled yet.",
    };
  }

  if (!completedInFpt) {
    return {
      invoiceReady: false,
      completedInFpt: false,
      alreadyInvoiced: false,
      invoiceBlockedReason: "Blocked: not complete in FPT.",
    };
  }

  return {
    invoiceReady: true,
    completedInFpt: true,
    alreadyInvoiced: false,
    invoiceReadyReason: "Complete in FPT, awarded, not invoiced.",
  };
}
